'use client';
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useEffect,
} from 'react';
import { Button, Icon } from '@ui/components';
import dynamic from 'next/dynamic';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from '../../components/ui/carousel';
import { Drawer, DrawerContent, DrawerOverlay, DrawerPortal } from '../../components/ui/drawer';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const ReactPlayer = dynamic(() => import('react-player'), { ssr: false });

interface MediaPreview {
  src: string;
  isVideo: boolean;
  name?: string;
}

interface ImagePreviewContextProps {
  setImagePreview: (src: string) => void;
  setMediaPreview: (src: string, isVideo: boolean) => void;
  setCarouselPreview: (items: MediaPreview[], startIndex: number) => void;
  closePreview: () => void;
}

const ImagePreviewContext = createContext<ImagePreviewContextProps | undefined>(undefined);

export const useImagePreviewContext = (): ImagePreviewContextProps => {
  const context = useContext(ImagePreviewContext);
  if (!context) {
    throw new Error('useImagePreviewContext must be used within an ImagePreviewProvider');
  }
  return context;
};

interface ImagePreviewProviderProps {
  children: ReactNode;
}

const CarouselSlide = ({
  media,
  index,
  activeIndex,
  api,
  touchCountRef,
  isPinchRef,
}: {
  media: MediaPreview;
  index: number;
  activeIndex: number;
  api: CarouselApi | undefined;
  touchCountRef: React.MutableRefObject<number>;
  isPinchRef: React.MutableRefObject<boolean>;
}) => {
  const [scale, setScale] = useState(1);
  const isZoomed = scale > 1;

  const handleZoom = useCallback((ref: any) => {
    setScale(ref.state.scale);
  }, []);

  return (
    <div className="flex h-full w-full items-center justify-center overflow-hidden">
      {media.isVideo ? (
        <div className="relative w-full aspect-video max-w-4xl">
          <ReactPlayer
            url={media.src}
            controls
            playing={activeIndex === index}
            width="100%"
            height="100%"
            style={{ position: 'absolute', top: 0, left: 0 }}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  playsInline: true,
                },
              },
            }}
          />
        </div>
      ) : (
        <TransformWrapper
          initialScale={1}
          minScale={1}
          maxScale={8}
          centerOnInit
          panning={{ disabled: !isZoomed }}
          doubleClick={{
            step: 0.7,
            mode: 'toggle',
          }}
          onZoom={handleZoom}
          onZoomStop={(ref) => {
            const currentScale = ref.state.scale;
            setScale(currentScale);
            if (isPinchRef.current) {
              ref.resetTransform(300);
              setScale(1);
            }
            if (api) {
              api.reInit({ watchDrag: currentScale <= 1 });
            }
            isPinchRef.current = false;
          }}>
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: 'calc(100vh - 80px)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              touchAction: isZoomed ? 'none' : 'pan-y pinch-zoom',
            }}
            contentStyle={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
            }}>
            <div
              onTouchStart={(e) => {
                touchCountRef.current = e.touches.length;
              }}
              onTouchEnd={() => {
                touchCountRef.current = 0;
              }}
              className="h-full w-full flex items-center justify-center">
              <img
                src={media.src}
                alt={media.name || 'Preview'}
                className="max-h-full max-w-full object-contain cursor-zoom-in"
              />
            </div>
          </TransformComponent>
        </TransformWrapper>
      )}
    </div>
  );
};

export const ImagePreviewProvider: React.FC<ImagePreviewProviderProps> = ({ children }) => {
  const [mediaList, setMediaList] = useState<MediaPreview[] | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [initialIndex, setInitialIndex] = useState(0);
  const [api, setApi] = useState<CarouselApi>();
  const touchCountRef = React.useRef(0);
  const isPinchRef = React.useRef(false);

  const setCarouselPreview = useCallback((items: MediaPreview[], startIndex: number) => {
    setMediaList(items);
    setActiveIndex(startIndex);
    setInitialIndex(startIndex);
  }, []);

  const setImagePreview = useCallback(
    (src: string) => {
      setCarouselPreview([{ src, isVideo: false }], 0);
    },
    [setCarouselPreview],
  );

  const setMediaPreview = useCallback(
    (src: string, isVideo: boolean) => {
      setCarouselPreview([{ src, isVideo }], 0);
    },
    [setCarouselPreview],
  );

  const closePreview = useCallback(() => {
    setMediaList(null);
    setApi(undefined);
  }, []);

  useEffect(() => {
    const isLocked = mediaList && mediaList.length > 0;
    if (isLocked) {
      document.body.classList.add('no-scroll');
      return () => {
        document.body.classList.remove('no-scroll');
      };
    }
  }, [!!mediaList?.length]);

  useEffect(() => {
    if (!api) return;

    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
    };

    api.on('select', onSelect);
    api.on('reInit', onSelect);

    return () => {
      api.off('select', onSelect);
      api.off('reInit', onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!mediaList) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closePreview();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [mediaList, closePreview]);

  const carouselOpts = React.useMemo(
    () => ({
      loop: true,
      dragThreshold: 8,
      startIndex: initialIndex,
    }),
    [initialIndex],
  );

  return (
    <ImagePreviewContext.Provider
      value={{ setImagePreview, setMediaPreview, setCarouselPreview, closePreview }}>
      {children}
      <Drawer open={!!mediaList?.length} onOpenChange={(open) => !open && closePreview()}>
        <DrawerPortal>
          <DrawerOverlay className="bg-black/95 backdrop-blur-sm z-[99998]" />
          <DrawerContent
            hideDragHandle
            className="flex h-[100dvh] flex-col border-none bg-transparent dark:bg-transparent z-[99999] rounded-none outline-none">
            <div className="flex h-full flex-col">
              {mediaList && mediaList.length > 0 && (
                <>
                  <div className="absolute top-8 left-8 z-50 text-white text-xl font-semibold tracking-tight opacity-90 drop-shadow-md">
                    {activeIndex + 1} / {mediaList.length}
                  </div>

                  <Button
                    type="icon"
                    className="absolute right-8 top-8 z-50 bg-white/20 hover:bg-white/40 text-white rounded-full transition-all w-12 h-12 shadow-lg"
                    onClick={closePreview}>
                    <Icon type="close" className="w-6 h-6" />
                  </Button>

                  <div className="flex-1 flex items-center justify-center p-4">
                    <Carousel
                      setApi={setApi}
                      opts={carouselOpts}
                      className="w-full h-full max-w-6xl relative">
                      <CarouselContent className="items-center h-full">
                        {mediaList.map((media, index) => (
                          <CarouselItem
                            key={index}
                            className="flex items-center justify-center h-full">
                            <CarouselSlide
                              media={media}
                              index={index}
                              activeIndex={activeIndex}
                              api={api}
                              touchCountRef={touchCountRef}
                              isPinchRef={isPinchRef}
                            />
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      {mediaList.length > 1 && (
                        <>
                          <CarouselPrevious className="hidden md:flex -left-20 lg:-left-32 bg-white/20 hover:bg-white/40 border-none text-white w-16 h-16 rounded-full transition-all scale-110 shadow-xl" />
                          <CarouselNext className="hidden md:flex -right-20 lg:-right-32 bg-white/20 hover:bg-white/40 border-none text-white w-16 h-16 rounded-full transition-all scale-110 shadow-xl" />
                        </>
                      )}
                    </Carousel>
                  </div>
                </>
              )}
            </div>
          </DrawerContent>
        </DrawerPortal>
      </Drawer>
    </ImagePreviewContext.Provider>
  );
};
